import { useQuery } from "@tanstack/react-query";
import { FALLBACK_INDUSTRY_CODE } from "@app/shared";
import { adminApi } from "./api";
import { Card, PageTitle } from "../../components/ui";
import { QueryView } from "../../components/states";

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <Card className="text-center">
      <p className={`text-3xl font-extrabold ${tone}`}>{value}</p>
      <p className="mt-1 text-xs font-bold text-slate-500">{label}</p>
    </Card>
  );
}

// 業種別の企業数（§2.4/§3.1 の運用指標）。other の割合が高いほど分類が実態に合っていない兆候。
function IndustryBreakdown({
  industries,
}: {
  industries: { code: string; label: string; count: number }[];
}) {
  const total = industries.reduce((s, r) => s + r.count, 0);
  const other = industries.find((r) => r.code === FALLBACK_INDUSTRY_CODE)?.count ?? 0;
  const otherPct = total > 0 ? Math.round((other / total) * 100) : 0;
  const max = Math.max(1, ...industries.map((r) => r.count));
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-sm font-bold text-slate-500">業種別の企業数（承認済み）</h3>
        {otherPct >= 20 && total > 0 && (
          <span className="rounded-pill bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700">
            「その他」が {otherPct}% — 分類の見直しを検討
          </span>
        )}
      </div>
      <Card>
        <div className="space-y-1.5">
          {industries.map((r) => (
            <div key={r.code} className="flex items-center gap-2 text-sm">
              <span className="w-28 shrink-0 text-slate-600">{r.label}</span>
              <div className="h-3 flex-1 rounded-pill bg-sky-light">
                <div
                  className={`h-3 rounded-pill ${r.code === FALLBACK_INDUSTRY_CODE ? "bg-amber-400" : "bg-brand"}`}
                  style={{ width: `${(r.count / max) * 100}%` }}
                />
              </div>
              <span className="w-8 shrink-0 text-right tabular-nums text-slate-500">{r.count}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export function AdminDashboardPage() {
  const query = useQuery({ queryKey: ["admin", "dashboard"], queryFn: adminApi.dashboard });

  return (
    <div className="space-y-6">
      <PageTitle title="ダッシュボード" subtitle="全体の状況をひと目で確認できます。" />
      <QueryView query={query}>
        {(d) => (
          <div className="space-y-6">
            <div>
              <h3 className="mb-2 text-sm font-bold text-slate-500">企業</h3>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="審査待ち" value={d.companies.pending} tone="text-amber-500" />
                <Stat label="承認済み" value={d.companies.approved} tone="text-emerald-500" />
                <Stat label="却下" value={d.companies.rejected} tone="text-rose-400" />
              </div>
            </div>
            <div>
              <h3 className="mb-2 text-sm font-bold text-slate-500">申込</h3>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Stat label="申込中" value={d.applications.applied} tone="text-brand" />
                <Stat label="承認済み" value={d.applications.approved} tone="text-emerald-500" />
                <Stat label="却下" value={d.applications.rejected} tone="text-rose-400" />
                <Stat label="取消" value={d.applications.cancelled} tone="text-slate-400" />
              </div>
              {d.applications.unnotified > 0 && (
                <div className="mt-3 rounded-soft border border-amber-300 bg-amber-50 p-3 text-sm font-bold text-amber-700">
                  ⚠ 企業へ未通知の申込が {d.applications.unnotified}{" "}
                  件あります（15分以上メール未達）。メール送信の障害が疑われます —
                  送信元ドメインの認証（SPF/DKIM/DMARC）と Resend
                  の状態を確認してください。各申込は該当校が自分の申込一覧から再送できます。
                </div>
              )}
            </div>
            <div>
              <h3 className="mb-2 text-sm font-bold text-slate-500">学校</h3>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="登録学校数" value={d.schools.total} tone="text-brand-dark" />
              </div>
            </div>
            <IndustryBreakdown industries={d.industries} />
          </div>
        )}
      </QueryView>
    </div>
  );
}
